// facetModelAdapter.js
//
// Rebuilds the *minimal* runtime-shaped polygon objects that
// generateFacetReport() reads from the global POLYGONS array, using only the
// persisted `model_data` (the saved schema produced by transformPolygonData in
// SavaData.js). This lets the Facet report run WITHOUT booting the 3D Designer.
//
// generateFacetReport only consumes these fields per polygon:
//   - polygon.edges[].point1/point2  (x,y,z)      -> 2D points + ridge markers
//   - polygon.obstructions[].cords   (x,y[,z])    -> obstacle outlines
//   - polygon.setBacks.{isSet, edgesData[].point1}-> setback outline
//   - polygon.details.{pitch, area, azimu}        -> facet stats
//   - polygon.label (string)                       -> facet label
// The live mesh (polygon.shape.geometry) is only a fallback in
// getSegmentPoints2d when edges < 3, so it is not required here.
//
// Saved schema (per polygon in model_data.polygons):
//   { label, polygon:[{x,y,z}], setbacks:[{vertex:{x,y,z}, value}],
//     obstruction:[{polygon:[{x,y,z}], type, height, offset, isFilled}],
//     detail:{...}, ... }

const toXYZ = (p) => ({
  x: Number(p?.x),
  y: Number(p?.y),
  z: Number(p?.z),
});

/**
 * Build edges as consecutive pairs of the saved vertex ring. The saved
 * `polygon` array is exactly the ordered list of each original edge.point1
 * (see SavaData.js), so consecutive pairs faithfully reconstruct the edges.
 */
const ringToEdges = (ring) => {
  if (!Array.isArray(ring) || ring.length < 2) return [];
  const n = ring.length;
  const edges = [];
  for (let i = 0; i < n; i++) {
    const p1 = ring[i];
    const p2 = ring[(i + 1) % n];
    edges.push({ point1: toXYZ(p1), point2: toXYZ(p2) });
  }
  return edges;
};

const reconstructPolygonForFacet = (saved) => {
  const ring = Array.isArray(saved?.polygon) ? saved.polygon : [];

  const obstructions = (Array.isArray(saved?.obstruction) ? saved.obstruction : []).map(
    (obs) => ({
      cords: (Array.isArray(obs?.polygon) ? obs.polygon : []).map(toXYZ),
      type: obs?.type,
      height: obs?.height,
      offset: obs?.offset,
      isFilled: obs?.isFilled === true,
    })
  );

  const savedSetbacks = Array.isArray(saved?.setbacks) ? saved.setbacks : [];
  const setBacks = {
    isSet: savedSetbacks.length > 0,
    edgesData: savedSetbacks.map((s) => ({ point1: toXYZ(s?.vertex) })),
  };

  return {
    label: typeof saved?.label === "string" ? saved.label : undefined,
    details: saved?.detail || saved?.details || {},
    edges: ringToEdges(ring),
    obstructions,
    setBacks,
  };
};

/**
 * @param {string|object} modelData - currentDesign.model_data (JSON string or parsed)
 * @returns {Array} runtime-shaped polygon objects for generateFacetReport
 */
export const buildFacetPolygonsFromModelData = (modelData) => {
  let parsed = modelData;
  if (typeof modelData === "string") {
    parsed = JSON.parse(modelData);
  }
  const savedPolygons = Array.isArray(parsed) ? parsed : parsed?.polygons;
  if (!Array.isArray(savedPolygons)) return [];
  return savedPolygons
    .map(reconstructPolygonForFacet)
    .filter((p) => Array.isArray(p.edges) && p.edges.length >= 3);
};

export default buildFacetPolygonsFromModelData;
